import React, { useState } from "react";
import { Link } from "react-router-dom";
import { workoutPageHref } from "@/features/accountNavV5/model/siteWorkoutPages";
import { APP_PLAN_WIZARD_URL, APP_WINDOW_PLAN_WIZARD, isAppWindow } from "@/shared/lib/appWindow";
import { useNeedsPlan } from "../lib/needsPlan";
import { planHintTexts } from "../model/texts";
import s from "./PlanHint.module.scss";

interface Props {
    /** Язык сайта из адреса (ru, en, cn…). */
    siteLang: string;
    /** Свой вид — только тогда подсказка вообще спрашивает сервер и может появиться. */
    self: boolean;
    /** row — строкой внутри шкалы «Тренировки»; card — отдельной плашкой (компьютер, «История баллов»). */
    variant?: "row" | "card";
    className?: string;
}

/**
 * «Создайте план тренировок — и тренировки начнут приносить баллы» + «Составить план ›»
 * (владелец 11.10, пункт 3) — у баллов «Тренировок» своего профиля и в «Истории баллов», только
 * самому человеку и только без действующего плана (GET /api/plan-reminder/me).
 *
 * Куда ведёт кнопка: план составляют в приложении. В браузере — туда, где на сайте план (сейчас —
 * карточка приложения «Тренировки», workoutPageHref: рубильник SITE_WORKOUT_PAGES_HIDDEN). В окне
 * приложения (?source=app) — сразу в его конструктор (myrevive://app/plan_wizard), но только когда
 * выпущенная сборка эту ссылку знает (рубильник APP_WINDOW_PLAN_WIZARD) и только на Android; иначе —
 * строка без кнопки: страница «Тренировки» в окне приложения — лишь «Открыть в приложении», а старая
 * сборка по ссылке просто вернёт в приложение — кнопка, которая ничего не делает, хуже никакой.
 */
export const PlanHint: React.FC<Props> = ({ siteLang, self, variant = "row", className }) => {
    const needs = useNeedsPlan(self);
    const [appWindow] = useState(isAppWindow);
    const [wizardLink] = useState(
        () => appWindow && APP_WINDOW_PLAN_WIZARD && /Android/i.test((typeof navigator !== "undefined" && navigator.userAgent) || ""),
    );
    if (!self || !needs) return null;
    const T = planHintTexts(siteLang);
    const cls = `${s.hint} ${variant === "card" ? s.card : s.row}${className ? ` ${className}` : ""}`;
    // Пробел перед «›» в inline-flex съедается — отступ у самой стрелки.
    const label = (
        <>
            {T.go}
            <span className={s.chev} aria-hidden="true">
                ›
            </span>
        </>
    );
    return (
        <div className={cls} data-testid="plan-hint">
            <span className={s.text}>{T.text}</span>
            {appWindow ? (
                wizardLink && (
                    <a className={s.go} href={APP_PLAN_WIZARD_URL} data-testid="plan-hint-go">
                        {label}
                    </a>
                )
            ) : (
                <Link className={s.go} to={workoutPageHref("plan", siteLang)} data-testid="plan-hint-go">
                    {label}
                </Link>
            )}
        </div>
    );
};

export default PlanHint;
